'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useLayoutEffect } from 'react';
import { getFocusableElements } from '@/app/components/overlays/utils/focusable';
interface UseFocusTrapProps {
  containerRef: RefObject<HTMLElement | null>;
  startGuardRef: RefObject<HTMLDivElement | null>;
  endGuardRef: RefObject<HTMLDivElement | null>;
  enabled: boolean;
}

/**
 * Trap focus inside the container using dummy focus guards (start/end).
 * No auto focus logic included.
 */
export const useFocusTrap = t0 => {
  const $ = _c(6);
  const {
    containerRef,
    startGuardRef,
    endGuardRef,
    enabled
  } = t0;
  let t1;
  let t2;
  if ($[0] !== containerRef || $[1] !== enabled || $[2] !== endGuardRef || $[3] !== startGuardRef) {
    t1 = () => {
      if (!enabled || !containerRef.current) {
        return;
      }
      const container = containerRef.current;
      const startEl = startGuardRef.current;
      const endEl = endGuardRef.current;
      const focusables = getFocusableElements(container);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const handleStartGuardFocus = () => {
        last?.focus({
          preventScroll: true
        });
      };
      const handleEndGuardFocus = () => {
        first?.focus({
          preventScroll: true
        });
      };
      startEl?.addEventListener("focus", handleStartGuardFocus);
      endEl?.addEventListener("focus", handleEndGuardFocus);
      return () => {
        startEl?.removeEventListener("focus", handleStartGuardFocus);
        endEl?.removeEventListener("focus", handleEndGuardFocus);
      };
    };
    t2 = [containerRef, startGuardRef, endGuardRef, enabled];
    $[0] = containerRef;
    $[1] = enabled;
    $[2] = endGuardRef;
    $[3] = startGuardRef;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useLayoutEffect(t1, t2);
};